/**
 * Photon address lookup for checkout billing/shipping.
 * The search field, manual link, summary, and address lines stay mounted.
 */

function getConfig() {
	return window.doneCommerce || {};
}

function debounce(fn, wait) {
	let timer;
	return (...args) => {
		window.clearTimeout(timer);
		timer = window.setTimeout(() => fn(...args), wait);
	};
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function getDeliveryMethod() {
	const form = document.querySelector('[data-dc-checkout]');
	return form?.dataset.deliveryMethod || 'delivery';
}

function isDifferentBilling() {
	return Boolean(document.querySelector('[data-dc-checkout]')?.classList.contains('is-billing-different'));
}

function fieldValue(id) {
	const el = document.getElementById(id);
	return el ? String(el.value || '').trim() : '';
}

function fieldHasValue(id) {
	return fieldValue(id) !== '';
}

function addressLooksFilled(prefix) {
	return fieldHasValue(`${prefix}_address_1`) || fieldHasValue(`${prefix}_city`);
}

function countryLabel(prefix) {
	const i18n = getConfig().i18n || {};
	const fallback = i18n.australia || 'Australia';
	const el = document.getElementById(`${prefix}_country`);
	if (!el) {
		return fallback;
	}

	if (el.tagName === 'SELECT') {
		const option = el.options[el.selectedIndex];
		const label = String(option?.text || '').trim();
		if (label) {
			return label;
		}
	}

	const code = String(el.value || '').trim().toUpperCase();
	if (code === '' || code === 'AU') {
		return fallback;
	}

	return code;
}

function addressParts(prefix) {
	const line = [fieldValue(`${prefix}_street_number`), fieldValue(`${prefix}_address_1`)].filter(Boolean).join(' ');
	const street = [line, fieldValue(`${prefix}_address_2`)].filter(Boolean).join(', ');
	const city = fieldValue(`${prefix}_city`);
	const region = [fieldValue(`${prefix}_state`), fieldValue(`${prefix}_postcode`)].filter(Boolean).join(' ');
	const locality = [city, region].filter(Boolean).join(' ');
	const country = street || locality ? countryLabel(prefix) : '';
	return { street, locality, country };
}

function formattedLabel(prefix) {
	const { street, locality, country } = addressParts(prefix);
	return [street, locality, country].filter(Boolean).join(', ');
}

function setFieldValue(id, value) {
	const el = document.getElementById(id);
	if (!el) {
		return;
	}

	const next = value ?? '';
	if (el.tagName === 'SELECT' && typeof window.jQuery !== 'undefined') {
		const $el = window.jQuery(el);
		if ($el.val() !== next) {
			$el.val(next).trigger('change');
		}
		return;
	}

	el.value = next;
	el.dispatchEvent(new Event('input', { bubbles: true }));
	el.dispatchEvent(new Event('change', { bubbles: true }));
}

function summaryHeadingText(prefix) {
	const i18n = getConfig().i18n || {};
	if (prefix === 'billing') {
		return i18n.weWillBillTo || 'We will bill to';
	}
	return i18n.weWillDeliverTo || 'We will deliver to';
}

function ensureAddressSummary() {
	return null;
}

function billingLookupAnchor(container) {
	return (
		container.querySelector('[data-billing-business-fields]') ||
		container.querySelector('.dc-billing-business') ||
		document.getElementById('billing_abn_field') ||
		document.getElementById('billing_company_field') ||
		container.querySelector(':scope > h3, :scope > [data-delivery-title]')
	);
}

function nestLookupResults(wrap) {
	const field = wrap?.querySelector('.dc-address-lookup__field');
	const results = wrap?.querySelector('[data-address-lookup-results]');
	if (field && results && results.parentElement !== field) {
		field.appendChild(results);
	}
}

function manualToggleLabel(expanded) {
	const i18n = getConfig().i18n || {};
	return expanded
		? i18n.hideAddressFields || 'Hide address fields'
		: i18n.enterAddressManually || 'Enter address manually';
}

function setManualButtonLabel(manual, expanded) {
	const text = manual?.querySelector('.dc-address-lookup__manual-text');
	const label = manualToggleLabel(expanded);
	if (text && text.textContent !== label) {
		text.textContent = label;
	}
}

function ensureManualButton(wrap) {
	if (!wrap) {
		return null;
	}

	let manual = wrap.querySelector('[data-address-lookup-manual]');

	if (!manual) {
		manual = document.createElement('button');
		manual.type = 'button';
		manual.className = 'dc-address-lookup__manual';
		manual.setAttribute('data-address-lookup-manual', '');
		wrap.appendChild(manual);
	}

	// Layout lives on a span. WebKit does not lay out flex items inside <button>.
	let row = manual.querySelector(':scope > .dc-address-lookup__manual-row');
	if (!row) {
		row = document.createElement('span');
		row.className = 'dc-address-lookup__manual-row';
		const text = document.createElement('span');
		text.className = 'dc-address-lookup__manual-text';
		const mark = document.createElement('span');
		mark.className = 'dc-address-lookup__manual-mark';
		mark.setAttribute('aria-hidden', 'true');
		row.append(text, mark);
		manual.replaceChildren(row);
	}

	if (!manual.hasAttribute('aria-expanded')) {
		manual.setAttribute('aria-expanded', 'false');
	}

	const text = row.querySelector('.dc-address-lookup__manual-text');
	if (text && !text.textContent) {
		text.textContent = manualToggleLabel(manual.getAttribute('aria-expanded') === 'true');
	}

	return manual;
}

function syncManualButton(container, prefix) {
	const wrap = document.querySelector(`[data-address-lookup="${prefix}"]`);
	const manual = ensureManualButton(wrap);
	if (!manual) {
		return;
	}

	const expanded = container.classList.contains('is-address-expanded');
	manual.setAttribute('aria-expanded', expanded ? 'true' : 'false');
	manual.classList.remove('is-open');
	setManualButtonLabel(manual, expanded);
}

function ensureAddressLookup(container, prefix) {
	if (!container) {
		return null;
	}

	let wrap = document.querySelector(`[data-address-lookup="${prefix}"]`);
	if (wrap?.closest('[data-address-modal]')) {
		nestLookupResults(wrap);
		ensureManualButton(wrap);
		return wrap;
	}
	if (!wrap) {
		const i18n = getConfig().i18n || {};
		wrap = document.createElement('div');
		wrap.className = 'dc-address-lookup form-row';
		wrap.setAttribute('data-address-lookup', prefix);
		wrap.innerHTML = `
			<div class="dc-address-lookup__field">
				<input type="text" class="input-text dc-address-lookup__input" autocomplete="off" aria-autocomplete="list" aria-expanded="false" role="combobox" data-address-lookup-input>
				<label>${escapeHtml(i18n.addressLookup || 'Start typing your address')}</label>
				<span class="dc-address-lookup__spinner" data-address-lookup-spinner hidden aria-hidden="true"></span>
				<ul class="dc-address-lookup__results" data-address-lookup-results role="listbox" hidden></ul>
			</div>
			<button type="button" class="dc-address-lookup__manual" data-address-lookup-manual aria-expanded="false">
				<span class="dc-address-lookup__manual-row">
					<span class="dc-address-lookup__manual-text">${escapeHtml(i18n.enterAddressManually || 'Enter address manually')}</span>
					<span class="dc-address-lookup__manual-mark" aria-hidden="true"></span>
				</span>
			</button>
		`;
	}

	nestLookupResults(wrap);
	ensureManualButton(wrap);

	const anchor =
		prefix === 'billing'
			? billingLookupAnchor(container)
			: container.querySelector(':scope > h3, :scope > [data-delivery-title]');
	if (anchor && wrap.previousElementSibling !== anchor) {
		anchor.insertAdjacentElement('afterend', wrap);
	} else if (!wrap.parentElement) {
		container.insertBefore(wrap, container.firstChild);
	}

	ensureAddressSummary(container, prefix);
	return wrap;
}

function partsFromResult(result) {
	if (!result || typeof result !== 'object') {
		return { street: '', locality: '', country: '' };
	}

	const number = String(result.housenumber || '').trim();
	const line = streetWithoutNumber(result.address_1 || '', number);
	const street = [number, line].filter(Boolean).join(' ');
	const city = String(result.city || '').trim();
	const region = [String(result.state || '').trim(), String(result.postcode || '').trim()].filter(Boolean).join(' ');
	const locality = [city, region].filter(Boolean).join(' ');
	const i18n = getConfig().i18n || {};
	const countryCode = String(result.country || 'AU').trim().toUpperCase();
	const country =
		street || locality
			? countryCode === '' || countryCode === 'AU'
				? i18n.australia || 'Australia'
				: countryCode
			: '';
	return { street, locality, country };
}

function writeSummary(summary, street, locality, country = '') {
	if (!summary) {
		return;
	}

	if (!summary.querySelector('[data-address-summary-country]')) {
		const countryEl = document.createElement('p');
		countryEl.className = 'dc-address-summary__country';
		countryEl.setAttribute('data-address-summary-country', '');
		countryEl.hidden = true;
		summary.appendChild(countryEl);
	}

	const streetEl = summary.querySelector('[data-address-summary-street]');
	const localityEl = summary.querySelector('[data-address-summary-locality]');
	const countryEl = summary.querySelector('[data-address-summary-country]');
	if (streetEl) {
		streetEl.textContent = street;
		streetEl.hidden = street === '';
	}
	if (localityEl) {
		localityEl.textContent = locality;
		localityEl.hidden = locality === '';
	}
	if (countryEl) {
		countryEl.textContent = country;
		countryEl.hidden = country === '';
	}
	const empty = street === '' && locality === '';
	summary.hidden = empty;
	const block = summary.closest('[data-address-summary-block]');
	if (block) {
		block.hidden = empty;
	}
}

function syncSummary(container, prefix, result = null) {
	ensureAddressSummary(container, prefix);
	const summary = container.querySelector(`[data-address-summary-block="${prefix}"] [data-address-summary]`);
	if (!summary) {
		return;
	}

	const wrap = container.querySelector(`[data-address-lookup="${prefix}"]`);
	const fromFields = addressParts(prefix);
	const fromResult = partsFromResult(result);
	let street = fromFields.street || fromResult.street;
	let locality = fromFields.locality || fromResult.locality;
	let country = fromFields.country || fromResult.country;

	if (street || locality) {
		if (wrap) {
			wrap.dataset.dcAddressStreet = street;
			wrap.dataset.dcAddressLocality = locality;
			wrap.dataset.dcAddressCountry = country;
		}
		if (!country) {
			country = countryLabel(prefix);
		}
	} else if (wrap && container.classList.contains('is-address-expanded')) {
		// Keep the chosen address after select when fields are mid-refresh or absent.
		street = wrap.dataset.dcAddressStreet || '';
		locality = wrap.dataset.dcAddressLocality || '';
		country = wrap.dataset.dcAddressCountry || '';
	}

	writeSummary(summary, street, locality, country);
}

function rememberSearchLabel(wrap, prefix, force = false) {
	const input = wrap?.querySelector('[data-address-lookup-input]');
	if (!input || document.activeElement === input || !addressLooksFilled(prefix)) {
		return;
	}

	if (!force && String(input.value || '').trim() !== '') {
		return;
	}

	const label = formattedLabel(prefix);
	if (!label) {
		return;
	}

	input.value = label;
	wrap.classList.add('is-filled', 'is-floating');
}

function bindAddressSummary(container, prefix) {
	if (!container || container.dataset.dcSummaryBound === 'true') {
		return;
	}

	container.dataset.dcSummaryBound = 'true';
	const onEdit = (event) => {
		const id = event.target?.id || '';
		if (!id.startsWith(`${prefix}_`) || id.endsWith('_country')) {
			return;
		}
		syncSummary(container, prefix);
		if (event.type === 'change') {
			const wrap = container.querySelector(`[data-address-lookup="${prefix}"]`);
			rememberSearchLabel(wrap, prefix, true);
		}
	};

	container.addEventListener('input', onEdit);
	container.addEventListener('change', onEdit);
}

async function fetchSuggestions(query) {
	const config = getConfig();
	const response = await fetch(
		`${config.restUrl}done-commerce/v1/address-lookup?q=${encodeURIComponent(query)}`,
		{
			credentials: 'same-origin',
			headers: {
				'X-WP-Nonce': config.nonce || '',
			},
		}
	);

	if (!response.ok) {
		throw new Error('lookup failed');
	}

	const data = await response.json();
	return Array.isArray(data.results) ? data.results : [];
}

function typedStreetNumber(prefix) {
	const input = document.querySelector(`[data-address-lookup="${prefix}"] [data-address-lookup-input]`);
	const match = String(input?.value || '')
		.trim()
		.match(/^(\d+[A-Za-z]?(?:[/-]\d+[A-Za-z]?)?)\b/);
	return match ? match[1] : '';
}

function streetWithoutNumber(street, number) {
	const line = String(street || '').trim();
	const num = String(number || '').trim();
	if (num && line.toLowerCase().startsWith(`${num.toLowerCase()} `)) {
		return line.slice(num.length).trim();
	}
	return line;
}

function fillAddress(prefix, result) {
	const number = String(result.housenumber || '').trim() || typedStreetNumber(prefix);
	setFieldValue(`${prefix}_street_number`, number);
	setFieldValue(`${prefix}_address_1`, streetWithoutNumber(result.address_1 || '', number));
	setFieldValue(`${prefix}_address_2`, result.address_2 || '');
	setFieldValue(`${prefix}_city`, result.city || '');
	setFieldValue(`${prefix}_postcode`, result.postcode || '');

	const country = result.country || 'AU';
	const countryEl = document.getElementById(`${prefix}_country`);
	if (countryEl && countryEl.value !== country) {
		countryEl.value = country;
		if (typeof window.jQuery !== 'undefined') {
			window.jQuery(countryEl).trigger('change');
		} else {
			countryEl.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	// Country change rebuilds the state select from the previous value. Apply state after that.
	setFieldValue(`${prefix}_state`, result.state || '');
}

function expandAddress(container, prefix, result) {
	container.classList.add('is-address-expanded');
	container.classList.remove('is-address-collapsed');
	const lookup = container.querySelector(`[data-address-lookup="${prefix}"]`);
	if (lookup) {
		lookup.hidden = false;
	}
	if (result) {
		fillAddress(prefix, result);
	}
	syncManualButton(container, prefix);
	syncSummary(container, prefix, result);
	document.dispatchEvent(new CustomEvent('dc:address-expanded', { detail: { prefix } }));
}

function collapseAddress(container, prefix) {
	container.classList.remove('is-address-expanded');
	container.classList.add('is-address-collapsed');
	syncManualButton(container, prefix);
	syncSummary(container, prefix);
	document.dispatchEvent(new CustomEvent('dc:address-collapsed', { detail: { prefix } }));
}

function bindLookup(wrap, container, prefix) {
	if (!wrap || wrap.dataset.bound === 'true') {
		return;
	}
	wrap.dataset.bound = 'true';

	const input = wrap.querySelector('[data-address-lookup-input]');
	const results = wrap.querySelector('[data-address-lookup-results]');
	const spinner = wrap.querySelector('[data-address-lookup-spinner]');
	const manual = wrap.querySelector('[data-address-lookup-manual]');
	let activeIndex = -1;
	let items = [];

	const renderResults = (list) => {
		items = list;
		activeIndex = -1;
		if (!results) {
			return;
		}
		if (!list.length) {
			results.hidden = true;
			results.innerHTML = '';
			input?.setAttribute('aria-expanded', 'false');
			return;
		}
		results.innerHTML = list
			.map(
				(item, index) =>
					`<li role="option" class="dc-address-lookup__option" data-index="${index}" id="dc-addr-opt-${prefix}-${index}">${escapeHtml(item.label)}</li>`
			)
			.join('');
		results.hidden = false;
		input?.setAttribute('aria-expanded', 'true');
	};

	const selectIndex = (index) => {
		const item = items[index];
		if (!item) {
			return;
		}
		expandAddress(container, prefix, item);
		if (input) {
			input.value = item.label || formattedLabel(prefix);
			wrap.classList.add('is-filled', 'is-floating');
		}
		renderResults([]);
		syncSummary(container, prefix, item);
	};

	const runSearch = debounce(async () => {
		const query = String(input?.value || '').trim();
		if (query.length < 3) {
			renderResults([]);
			if (spinner) {
				spinner.hidden = true;
			}
			wrap.classList.remove('is-loading');
			return;
		}

		wrap.classList.add('is-loading');
		if (spinner) {
			spinner.hidden = false;
		}

		try {
			const list = await fetchSuggestions(query);
			renderResults(list);
		} catch {
			renderResults([]);
		} finally {
			wrap.classList.remove('is-loading');
			if (spinner) {
				spinner.hidden = true;
			}
		}
	}, 280);

	input?.addEventListener('input', () => {
		wrap.classList.toggle('is-filled', String(input.value || '').trim() !== '');
		runSearch();
	});
	input?.addEventListener('focus', () => {
		wrap.classList.add('is-floating');
	});
	input?.addEventListener('blur', () => {
		window.setTimeout(() => {
			renderResults([]);
			rememberSearchLabel(wrap, prefix, true);
			wrap.classList.toggle('is-filled', String(input.value || '').trim() !== '');
			wrap.classList.toggle('is-floating', String(input.value || '').trim() !== '');
		}, 150);
	});

	input?.addEventListener('keydown', (event) => {
		if (results?.hidden || !items.length) {
			return;
		}
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			activeIndex = Math.min(items.length - 1, activeIndex + 1);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			activeIndex = Math.max(0, activeIndex - 1);
		} else if (event.key === 'Enter' && activeIndex >= 0) {
			event.preventDefault();
			selectIndex(activeIndex);
			return;
		} else if (event.key === 'Escape') {
			renderResults([]);
			return;
		} else {
			return;
		}
		results.querySelectorAll('.dc-address-lookup__option').forEach((el, index) => {
			el.classList.toggle('is-active', index === activeIndex);
		});
	});

	results?.addEventListener('mousedown', (event) => {
		const option = event.target.closest('[data-index]');
		if (!option) {
			return;
		}
		event.preventDefault();
		selectIndex(Number(option.getAttribute('data-index')));
	});

	manual?.addEventListener('click', () => {
		if (container.classList.contains('is-address-expanded')) {
			collapseAddress(container, prefix);
			return;
		}
		expandAddress(container, prefix, null);
		document.getElementById(`${prefix}_address_1`)?.focus();
	});
}

function activeTargets() {
	const billing = document.querySelector('.dc-checkout .woocommerce-billing-fields');
	const shipping = document.querySelector('.dc-checkout .woocommerce-shipping-fields');
	const targets = [];
	if (billing) {
		targets.push({ container: billing, prefix: 'billing' });
	}
	if (shipping) {
		targets.push({ container: shipping, prefix: 'shipping' });
	}
	return targets;
}

function syncAddressLookup() {
	const checkout = document.querySelector('[data-dc-checkout], .dc-checkout');
	if (!checkout) {
		return;
	}

	activeTargets().forEach(({ container, prefix }) => {
		const wrap = ensureAddressLookup(container, prefix);
		if (!wrap) {
			return;
		}
		bindLookup(wrap, container, prefix);
		wrap.hidden = false;
		syncManualButton(container, prefix);
	});
}

export function initCheckoutAddressLookup() {
	const checkout = document.querySelector('[data-dc-checkout], .dc-checkout');
	if (!checkout) {
		return;
	}

	syncAddressLookup();

	const onUpdated = () => {
		window.requestAnimationFrame(() => syncAddressLookup());
	};
	document.body.addEventListener('updated_checkout', onUpdated);
	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).on('updated_checkout', onUpdated);
	}

	document.addEventListener('dc:delivery-method', () => {
		window.requestAnimationFrame(() => syncAddressLookup());
	});

	document.addEventListener('dc:address-expanded', () => {
		window.requestAnimationFrame(() => syncAddressLookup());
	});

	const observer = new MutationObserver(() => syncAddressLookup());
	observer.observe(checkout, { attributes: true, attributeFilter: ['class', 'data-delivery-method'] });
}
